import { THEME, NODE_TYPE_LABELS, LINK_TYPE_LABELS } from "../utils/theme";

/**
 * Sidebar — search, node type toggles, link type toggles, legend.
 */
export default function Sidebar({
  filters,
  onToggle,
  searchTerm,
  onSearch,
  nodeCount,
  linkCount,
  allNodes,
}) {
  return (
    <div style={styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={styles.icon}>{"\u2b21"}</div>
          <div>
            <div style={styles.title}>Network Map</div>
            <div style={styles.subtitle}>{nodeCount} nodes {"\u00b7"} {linkCount} connections</div>
          </div>
        </div>
        <input
          type="text"
          placeholder="Search nodes..."
          value={searchTerm}
          onChange={(e) => onSearch(e.target.value)}
          style={styles.search}
        />
      </div>

      {/* Node type filters */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Node Types</div>
        {Object.entries(NODE_TYPE_LABELS).map(([key, label]) => {
          const colors = THEME.nodeColors[key];
          const count = allNodes.filter((n) => n.type === key).length;
          return (
            <label key={key} style={styles.filterRow}>
              <div onClick={() => onToggle(key)} style={{
                ...styles.checkbox,
                border: `1.5px solid ${filters[key] ? colors.stroke : THEME.textDim}`,
                background: filters[key] ? colors.fill + "33" : "transparent",
              }}>
                {filters[key] && <span style={{ fontSize: 10, color: colors.stroke }}>{"\u2713"}</span>}
              </div>
              <span style={{
                width: 8, height: 8,
                borderRadius: key === "contact" ? "50%" : 2,
                background: colors.fill, marginRight: 2,
                display: "inline-block",
              }} />
              <span style={{ color: filters[key] ? THEME.text : THEME.textDim }}>{label}</span>
              <span style={styles.count}>{count}</span>
            </label>
          );
        })}
      </div>

      {/* Link type filters */}
      <div style={styles.section}>
        <div style={styles.sectionLabel}>Relationships</div>
        {Object.entries(LINK_TYPE_LABELS).map(([key, label]) => {
          const filterKey = key === "sector" ? "sector_link" : key;
          const color = THEME.linkColors[key];
          return (
            <label key={key} style={{ ...styles.filterRow, fontSize: 11.5 }}>
              <div onClick={() => onToggle(filterKey)} style={{
                ...styles.checkboxSmall,
                border: `1.5px solid ${filters[filterKey] ? color : THEME.textDim}`,
                background: filters[filterKey] ? color + "22" : "transparent",
              }}>
                {filters[filterKey] && <span style={{ fontSize: 9, color }}>{"\u2713"}</span>}
              </div>
              <span style={{ color: filters[filterKey] ? THEME.text : THEME.textDim }}>{label}</span>
            </label>
          );
        })}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.sectionLabel}>Legend</div>
        <div style={styles.legendGrid}>
          <span>{"\u2b21"} Sector</span><span>{"\u25c7"} Opportunity</span>
          <span>{"\u25ac"} Company</span><span>{"\u25cf"} Contact</span>
          <span style={styles.legendDivider}>
            {"\u2500\u2500"} Direct &nbsp; {"- -"} Referral &nbsp; {"\u00b7\u00b7\u00b7"} Shared
          </span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: {
    width: 280, minWidth: 280,
    background: THEME.surface,
    borderRight: `1px solid ${THEME.border}`,
    display: "flex", flexDirection: "column", overflow: "hidden",
  },
  header: {
    padding: "20px 18px 16px",
    borderBottom: `1px solid ${THEME.border}`,
  },
  icon: {
    width: 32, height: 32, borderRadius: 8,
    background: `linear-gradient(135deg, ${THEME.accent}, #7C3AED)`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14,
  },
  title: { fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" },
  subtitle: { fontSize: 11, color: THEME.textSecondary },
  search: {
    width: "100%", padding: "8px 12px",
    background: THEME.bg, border: `1px solid ${THEME.border}`,
    borderRadius: 6, color: THEME.text, fontSize: 12.5,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit",
  },
  section: {
    padding: "14px 18px",
    borderBottom: `1px solid ${THEME.border}`,
  },
  sectionLabel: {
    fontSize: 10, fontWeight: 600,
    textTransform: "uppercase", letterSpacing: "0.08em",
    color: THEME.textDim, marginBottom: 10,
  },
  filterRow: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "5px 0", cursor: "pointer", fontSize: 12.5,
  },
  checkbox: {
    width: 16, height: 16, borderRadius: 3,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s", cursor: "pointer",
  },
  checkboxSmall: {
    width: 14, height: 14, borderRadius: 3,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s", cursor: "pointer",
  },
  count: {
    marginLeft: "auto", fontSize: 11,
    color: THEME.textDim, fontFamily: "monospace",
  },
  legend: { padding: "14px 18px", marginTop: "auto" },
  legendGrid: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: "4px 12px", fontSize: 10.5, color: THEME.textSecondary,
  },
  legendDivider: {
    gridColumn: "1 / -1", marginTop: 4,
    borderTop: `1px solid ${THEME.border}`, paddingTop: 6,
  },
};
